import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Dispatch } from 'react';

import { ensureDefined } from '@e-socna/utils';
import {
  OrientationEnum,
  PaperFormatEnum,
  TemplateTypeEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReducerWithMiddleware } from '../../../hooks/useReducerWithMiddleware.hook';
import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { emptyMemosState } from '../../../forms/MemoBeforeSubmit';
import type { CompatibleSectionTemplates_SectionTemplates } from '../../../forms/CompatibleSectionTemplates/hooks/useFindAllCompatibleSectionTemplatesQuery.hook';
import type { GenericAdminFormValues } from '../../../forms/GenericAdmin';

import { DocumentEditorActionType } from '../enums/action-type.enum';
import { BackupMode } from '../enums/backup-mode.enum';
import { DocumentEditorInstantaneousContentActionType } from '../enums/instantaneous-content-action-type.enum';
import type { DocumentEditorTwoStepContentActionType } from '../enums/two-step-content-action-type.enum';
import type { FoldingMode } from '../enums/folding-mode.enum';
import type {
  DocumentEditorCmp_RecursiveSection,
  DocumentEditorCmp_SectionDisplayCondition,
} from '../fragments/recursive-section.fragment';
import type { TextualContent } from '../types/textual-content.type';
import type { DocumentEditorSimpleActions } from '../types/simple-actions.type';
import type { DocumentEditorState } from '../types/editor-state.type';
import type { DocumentEditorAction } from '../types/actions.type';
import type { DocumentEditorContentAction } from '../types/content-actions.type';
import type { DocumentEditorCmp_TemplateDependency } from '../queries/one-template-dependency.query';
import type { Editable } from '../types/editable.type';
import type { Getters, OnSaveHandler } from '../types/props.type';
import type { EnrichedSection } from '../types/enriched-section.type';

import { useDocumentEditorReducer } from './useDocumentEditorReducer.hook';

interface Params<E extends Editable> {
  readonly askMemoBeforeSave: boolean;
  readonly backupMode: BackupMode;
  readonly getters: Getters<E>;
  readonly handleSave: OnSaveHandler<E>;
  readonly initialEditable: E;
  readonly templateDependencies: readonly DocumentEditorCmp_TemplateDependency[];
}

export const useEditorActions = <E extends Editable>({
  initialEditable,
  templateDependencies,
  backupMode,
  askMemoBeforeSave,
  handleSave,
  getters,
}: Readonly<Params<E>>) => {
  const [saveMemos, setSaveMemos] = useState(emptyMemosState);
  const clearSaveMemos = useCallback(() => {
    setSaveMemos(emptyMemosState);
  }, []);

  const { generateId } = useIdFactory();

  const { contentOriginsCodeMap } = useReferencesContext();

  const documentEditorReducer = useDocumentEditorReducer<E>({
    initialEditable,
    getters,
    templateDependencies,
  });

  const handleAutoBackup = useCallback(
    (
      state: Readonly<DocumentEditorState<E>>,
      dispatch: Dispatch<DocumentEditorAction<E>>,
      action?: DocumentEditorAction<E> | DocumentEditorContentAction | null,
    ) => {
      if (!action?.autoBackup) {
        return;
      }

      handleSave(state.editable, saveMemos, () => {
        clearSaveMemos();
        dispatch({
          type: DocumentEditorActionType.BumpLatestSavedVersion,
          autoBackup: false,
        });
      });
    },
    [handleSave, clearSaveMemos, saveMemos],
  );

  const navigate = useNavigate();

  const handleNavigation = useCallback(
    (
      state: DocumentEditorState<E>,
      dispatch: Dispatch<DocumentEditorAction<E>>,
    ) => {
      if (!state.navigationTarget) {
        return;
      }

      void navigate(state.navigationTarget);

      dispatch({
        type: DocumentEditorActionType.ClearNavigationTarget,
        autoBackup: false,
      });
    },
    [navigate],
  );

  const [state, dispatch] = useReducerWithMiddleware<
    DocumentEditorState<E>,
    DocumentEditorAction<E> | DocumentEditorContentAction
  >(
    documentEditorReducer,
    {
      pastVersions: EMPTY_ARRAY,
      editable: initialEditable,
      futureVersions: EMPTY_ARRAY,
      isDrawerOpen: false,
      foldedSectionIds: EMPTY_ARRAY,
      currentVersion: 0,
      latestSavedVersion: undefined,
    },
    EMPTY_ARRAY,
    [
      ...(backupMode === BackupMode.Auto ? [handleAutoBackup] : EMPTY_ARRAY),
      handleNavigation,
    ],
  );

  const actions: DocumentEditorSimpleActions<E> = useMemo(
    () => ({
      openDrawer: () => {
        dispatch({
          type: DocumentEditorActionType.OpenDrawer,
          autoBackup: false,
        });
      },
      closeDrawer: () => {
        dispatch({
          type: DocumentEditorActionType.CloseDrawer,
          autoBackup: false,
        });
      },
      foldAllSections: (sectionIds: readonly Id[]) => {
        dispatch({
          type: DocumentEditorActionType.FoldAllSections,
          autoBackup: false,
          payload: { sectionIds },
        });
      },
      unfoldAllSections: () => {
        dispatch({
          type: DocumentEditorActionType.UnfoldAllSections,
          autoBackup: false,
        });
      },
      requestTwoStepContentAction: ({
        twoStepContentActionType,
        autoBackup,
        path,
        alreadyUsedIds,
        insertionPointRealPath,
        textualContent,
        onlyOneChoice,
        orientation,
        paperFormat,
      }: Readonly<{
        twoStepContentActionType: DocumentEditorTwoStepContentActionType;
        autoBackup: boolean;
        path: readonly number[];
        alreadyUsedIds: readonly Id[];
        insertionPointRealPath?: readonly number[];
        textualContent?: TextualContent;
        onlyOneChoice?: boolean;
        orientation?: OrientationEnum;
        paperFormat?: PaperFormatEnum;
      }>) => {
        dispatch({
          type: DocumentEditorActionType.RequestTwoStepContentAction,
          autoBackup: false,
          payload: {
            twoStepContentActionType,
            autoBackup,
            insertionPointRealPath,
            path,
            alreadyUsedIds,
            textualContent,
            onlyOneChoice,
            orientation,
            paperFormat,
          },
        });
      },
      fulfillTwoStepContentAction: ({
        content,
        contentOriginId,
      }: Readonly<{
        content:
          | TextualContent
          | readonly CompatibleSectionTemplates_SectionTemplates[];
        contentOriginId: Id;
      }>) => {
        const { currentTwoStepContentAction } = ensureDefined({
          currentTwoStepContentAction: state.currentTwoStepContentAction,
        });
        switch (contentOriginId) {
          case contentOriginsCodeMap.Special._id: {
            // FIXME:  Typescript error
            dispatch({
              type: currentTwoStepContentAction.twoStepContentActionType,
              autoBackup: currentTwoStepContentAction.autoBackup,
              payload: {
                path: currentTwoStepContentAction.path,
                sections: [
                  {
                    __typename: 'Section' as const,
                    ...(content as TextualContent),
                    _id: generateId(),
                    subSections: EMPTY_ARRAY,
                    contentOriginId: contentOriginsCodeMap.Special._id,
                    orientation: OrientationEnum.Portrait,
                    paperFormat: PaperFormatEnum.A4,
                    hasPagebreakAfter: false,
                    hasPagebreakBefore: false,
                  },
                ],
                insertionPointRealPath:
                  currentTwoStepContentAction.insertionPointRealPath,
              },
            });

            return;
          }
          case contentOriginsCodeMap.Catalog._id: {
            dispatch({
              type: currentTwoStepContentAction.twoStepContentActionType,
              autoBackup: currentTwoStepContentAction.autoBackup,
              payload: {
                path: currentTwoStepContentAction.path,
                sections: (
                  content as readonly CompatibleSectionTemplates_SectionTemplates[]
                ).map(
                  // FIXME:  Typescript error
                  (sectionTemplate): DocumentEditorCmp_RecursiveSection => ({
                    __typename: 'Section' as const,
                    _id: generateId(),
                    template: {
                      __typename: 'TemplateReference' as const,
                      _id: sectionTemplate._id,
                      type: TemplateTypeEnum.SectionTemplate,
                    },
                    contentOriginId: contentOriginsCodeMap.Catalog._id,
                    isVirtual: true,
                    isInsertionPoint: true,
                    orientation: sectionTemplate.virtualRoot.orientation,
                    paperFormat: sectionTemplate.virtualRoot.paperFormat,
                    hasPagebreakAfter:
                      sectionTemplate.virtualRoot.hasPagebreakAfter,
                    hasPagebreakBefore:
                      sectionTemplate.virtualRoot.hasPagebreakBefore,
                    subSections: EMPTY_ARRAY,
                  }),
                ),
                insertionPointRealPath:
                  currentTwoStepContentAction.insertionPointRealPath,
              },
            });

            return;
          }
          case contentOriginsCodeMap.Modified._id:
          default: {
            throw new Error('Invalid origin');
          }
        }
      },
      clearTwoStepContentAction: () => {
        dispatch({
          type: DocumentEditorActionType.ClearTwoStepContentAction,
          autoBackup: false,
        });
      },
      setFoldingMode: (id: Id, foldingMode: FoldingMode) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetFoldingMode,
          autoBackup: false,
          payload: { id, foldingMode },
        });
      },
      deleteSection: (
        path: readonly number[],
        insertionPointRealPath?: readonly number[],
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.Delete,
          autoBackup: true,
          payload: { path, insertionPointRealPath },
        });
      },
      reinitializeSection: (
        path: readonly number[],
        insertionPointRealPath?: readonly number[],
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.Reinitialize,
          autoBackup: true,
          payload: { path, insertionPointRealPath },
        });
      },
      duplicateSection: (
        path: readonly number[],
        insertionPointRealPath?: readonly number[],
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.Duplicate,
          autoBackup: true,
          payload: { path, insertionPointRealPath },
        });
      },
      setCustomPageBreakAfter: (
        insertionPointRealPath: readonly number[],
        id: Id,
        hasPagebreakAfter: boolean,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter,
          autoBackup: true,
          payload: { insertionPointRealPath, id, hasPagebreakAfter },
        });
      },
      setPageBreakAfter: (
        path: readonly number[],
        hasPagebreakAfter: boolean,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetPageBreakAfter,
          autoBackup: true,
          payload: { path, hasPagebreakAfter },
        });
      },
      setCustomPageBreakBefore: (
        insertionPointRealPath: readonly number[],
        id: Id,
        hasPagebreakBefore: boolean,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore,
          autoBackup: true,
          payload: { insertionPointRealPath, id, hasPagebreakBefore },
        });
      },
      setPageBreakBefore: (
        path: readonly number[],
        hasPagebreakBefore: boolean,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetPageBreakBefore,
          autoBackup: true,
          payload: { path, hasPagebreakBefore },
        });
      },
      setOrientation: (
        section: Readonly<EnrichedSection>,
        orientation: OrientationEnum,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetOrientation,
          autoBackup: true,
          payload: { section, orientation },
        });
      },
      setDisplayCondition: (
        path: readonly number[],
        displayCondition: DocumentEditorCmp_SectionDisplayCondition | null,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetDisplayCondition,
          autoBackup: true,
          payload: { path, displayCondition },
        });
      },
      setPaperFormat: (
        section: Readonly<EnrichedSection>,
        paperFormat: PaperFormatEnum,
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.SetPaperFormat,
          autoBackup: true,
          payload: { section, paperFormat },
        });
      },
      undo: () => {
        dispatch({
          type: DocumentEditorActionType.Undo,
          autoBackup: true,
        });
      },
      redo: () => {
        dispatch({
          type: DocumentEditorActionType.Redo,
          autoBackup: true,
        });
      },
      clearHistory: () => {
        dispatch({
          type: DocumentEditorActionType.ClearHistory,
          autoBackup: true,
        });
      },
      bumpLatestSavedVersion: () => {
        dispatch({
          type: DocumentEditorActionType.BumpLatestSavedVersion,
          autoBackup: false,
        });
      },
      clearNavigationTarget: () => {
        dispatch({
          type: DocumentEditorActionType.ClearNavigationTarget,
          autoBackup: false,
        });
      },
      updateInfo: (newEditable: Readonly<E>, autoBackup = true) => {
        dispatch({
          type: DocumentEditorActionType.UpdateInfo,
          autoBackup,
          payload: { newEditable },
        });
      },
      updateAdmin: (newAdminValues: Readonly<GenericAdminFormValues>) => {
        dispatch({
          type: DocumentEditorActionType.UpdateAdmin,
          autoBackup: true,
          payload: { newAdminValues },
        });
      },
      // eslint-disable-next-line max-params
      move: (
        oldPath: readonly number[],
        newPath: readonly number[],
        oldInsertionPointRealPath?: readonly number[],
        newInsertionPointRealPath?: readonly number[],
      ) => {
        dispatch({
          type: DocumentEditorInstantaneousContentActionType.Move,
          autoBackup: true,
          payload: {
            oldPath,
            newPath,
            oldInsertionPointRealPath,
            newInsertionPointRealPath,
          },
        });
      },
    }),
    [
      contentOriginsCodeMap.Catalog._id,
      contentOriginsCodeMap.Modified._id,
      contentOriginsCodeMap.Special._id,
      dispatch,
      state.currentTwoStepContentAction,
      generateId,
    ],
  );

  const saveSubmitHandler = useCallback(
    () =>
      handleSave(state.editable, saveMemos, () => {
        clearSaveMemos();
        actions.bumpLatestSavedVersion();
      }),
    [state.editable, handleSave, saveMemos, clearSaveMemos, actions],
  );

  const showMemoBeforeSave =
    backupMode === BackupMode.Manual && askMemoBeforeSave;

  const handleSaveAction = useCallback(() => {
    showMemoBeforeSave
      ? setSaveMemos((oldMemos) => ({
          ...oldMemos,
          isOpenMemoDialog: true,
        }))
      : saveSubmitHandler();
  }, [showMemoBeforeSave, saveSubmitHandler]);

  return {
    clearSaveMemos,
    actions,
    state,
    handleSaveAction,
    saveMemos,
    saveSubmitHandler,
    setSaveMemos,
    showMemoBeforeSave,
  };
};
